import { useEffect, useMemo, useState } from "react";

import {
  ContactResponses,
  SBContact,
  SBContactType,
  buildIndexedLayoutFieldName,
  createNewContactPlaceholder,
  defaultClientRoleId,
  getSentToIndividualClientIndex,
} from "@smart/bridge-smokeball-basic";
import { useAsync } from "@smart/itops-hooks-dom";
import { Modal } from "@smart/itops-sb-design-system-dom";
import { ButtonVariant } from "@smart/itops-sb-design-system-dom/lib/components/button/class-names";
import {
  LayoutContact,
  LayoutMatterItem,
  Matter,
  Roles,
} from "@smart/itops-smokeball-app-dom";
import {
  backoff,
  entriesOf,
  fromEntries,
  groupBy,
  isNotNullOrUndefined,
  pluralise,
  sortField,
} from "@smart/itops-utils-basic";
import {
  MatterSubmission,
  mapToMatterSubmission,
  useLoadMatterLayouts,
  useQueryMatterLayoutFields,
  useQuerySubmission,
  useUpdateResponseExistingItemIds,
  useUpdateResponseSyncStatus,
} from "@smart/manage-gql-client-dom";
import { Gql } from "@smart/manage-gql-operations-dom";
import {
  processResponses,
  useRoleContacts,
  useSyncToMatter,
} from "@smart/manage-hooks-dom";

import { ContactSelect } from "./contact-select";

const getIndexedName = (name: string, index: number) => `${name}-${index}`;

type RoleToReview = {
  roleName: string;
  contact: Partial<Record<SBContactType, Record<string, any>>>;
  index: number;
  responseUris: Set<string>;
  needReviewBeforeUpdate?: boolean;
  indexedLayoutFieldName?: string; // For layout contacts only
};

type RoleReviewInfo = {
  key: string;
  displayRoleName: string;
  currentContactId: string | undefined;
  hasRoleContact: boolean;
  role: RoleToReview;
};

const getRoleReviewInfo = ({
  role,
  roles,
  clientRoleName,
  roleNameToDisplayNameMap,
  rolesAndRelationships,
  layoutContacts,
}: {
  role: RoleToReview;
  roles: RoleToReview[];
  clientRoleName: string | undefined;
  roleNameToDisplayNameMap: Record<string, string>;
  rolesAndRelationships: Record<string, { contactId: string }[]>;
  layoutContacts: Record<string, LayoutContact>;
}): RoleReviewInfo | null => {
  const { roleName, index, needReviewBeforeUpdate, indexedLayoutFieldName } =
    role;
  const key = getIndexedName(roleName, index);

  const currentContactId =
    rolesAndRelationships[roleName]?.[index]?.contactId ||
    (indexedLayoutFieldName &&
      layoutContacts[indexedLayoutFieldName]?.contactId);
  const hasRoleContact = !!currentContactId;

  // Don't have to review contact if it already has an existing one and no need to review
  if (hasRoleContact && !needReviewBeforeUpdate) {
    return null;
  }

  const hasMultipleContacts =
    roles.filter((r) => r.roleName === roleName).length > 1;
  const isDefaultClientRole = roleName === defaultClientRoleId;
  const roleNameToUse = isDefaultClientRole
    ? clientRoleName || "Client"
    : roleName
        .split("/")
        .map((part) => roleNameToDisplayNameMap[roleName] || part)
        .join("/");

  const displayRoleName = hasMultipleContacts
    ? `${roleNameToUse} ${index + 1}`
    : roleNameToUse;

  return {
    key,
    displayRoleName,
    currentContactId,
    hasRoleContact,
    role,
  };
};

export type ContactReviewProps = {
  loading: boolean;
  onClose: () => void;
  matter: Matter | undefined;
  rolesAndRelationships: Record<string, { contactId: string }[]>;
  submission: MatterSubmission;
  sections: Gql.SectionFieldsFragment[] | undefined;
  groups: Gql.GroupFieldsFragment[] | undefined;
  fields: Gql.FieldFieldsFragment[] | undefined;
  formMatterTypeRepresentatives: string[] | undefined;
  layoutItems: LayoutMatterItem[] | undefined;
  getContact:
    | ((contactId: string) => Promise<SBContact | undefined>)
    | undefined;
  getRoles: (() => Promise<Roles | undefined>) | undefined;
  getLayoutContacts:
    | ((itemId: string) => Promise<{
        layoutContacts: LayoutContact[];
      }>)
    | undefined;
  updateResponseExistingItemIds: ReturnType<
    typeof useUpdateResponseExistingItemIds
  >[0];
  updateResponseSyncStatus: ReturnType<typeof useUpdateResponseSyncStatus>[0];
  querySubmission: ReturnType<typeof useQuerySubmission>;
  sync: ReturnType<typeof useSyncToMatter>["sync"];
  onSuccess: () => void;
  onFailure: () => void;
  openContact: ((id: string) => void) | undefined;
};

export const ContactReview = ({
  loading,
  onClose,
  matter,
  rolesAndRelationships,
  submission,
  sections,
  groups,
  fields,
  formMatterTypeRepresentatives,
  layoutItems,
  getContact,
  getRoles,
  getLayoutContacts,
  updateResponseExistingItemIds,
  updateResponseSyncStatus,
  querySubmission,
  sync,
  onSuccess,
  onFailure,
  openContact,
}: ContactReviewProps) => {
  const { client } = useRoleContacts();
  const [reviewResult, setReviewResult] = useState<Record<string, string>>({});
  const [autoResolvedKeys, setAutoResolvedKeys] = useState<Set<string>>(
    new Set(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const loadMatterLayoutFields = useQueryMatterLayoutFields();
  const [roles, setRoles] = useState<RoleToReview[]>([]);
  const [layoutContacts, setLayoutContacts] = useState<
    Record<string, LayoutContact>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const matterLayouts = useLoadMatterLayouts(
    {
      matterTypeIds: [matter?.matterTypeId || ""],
    },
    { skip: !matter?.matterTypeId },
  );
  const roleNameToDisplayNameMap = useMemo<Record<string, string>>(() => {
    if (!matterLayouts.result?.length) return {};

    return matterLayouts.result.reduce(
      (aggr, { id: roleName, name: displayName }) => ({
        ...aggr,
        [roleName]: displayName,
      }),
      {},
    );
  }, [matterLayouts.result]);

  useAsync(async () => {
    if (!sections || !groups || !fields) return;

    setIsLoading(true);

    const existingRoles = await getRoles?.();
    const sentToIndividualClientIndex = getSentToIndividualClientIndex(
      existingRoles?.roles || [],
      submission.recipient?.contactId,
    );
    const groupedResponses = await processResponses({
      clientRoleId: client?.role.name,
      sentToIndividualClientIndex,
      matter,
      groups: groups || [],
      fields: fields || [],
      sections: sections || [],
      submission,
      formMatterTypeRepresentatives,
      getLayoutFields: async ({ layoutId, providerId }) => {
        const matterFields = await loadMatterLayoutFields({
          matterLayoutId: layoutId,
          providerId,
        });

        return matterFields.map((f) => ({
          ...f,
          possibleValues: f.possibleValues || undefined,
          dataSourceMapping: f.dataSourceMapping || undefined,
        }));
      },
    });

    const getContacts = (contact: ContactResponses) => {
      // We don't do duplication check for the staff of a company
      if (contact.hasRelation) return [{ company: contact.company }];
      return contact.group?.length ? contact.group : [contact];
    };

    const rolesToReview: RoleToReview[] = [];

    entriesOf(groupedResponses.roles).forEach(([roleName, contact]) => {
      getContacts(contact).forEach((c, index) => {
        rolesToReview.push({
          roleName,
          contact: c,
          index,
          responseUris: groupedResponses.responseUris.roles[roleName],
          needReviewBeforeUpdate: contact.reviewBeforeUpdate?.[index],
        });
      });
    });

    entriesOf(groupedResponses.relationships).forEach(
      ([roleName, relationshipItems]) => {
        entriesOf(relationshipItems).forEach(([relationshipId, contact]) => {
          getContacts(contact).forEach((c, index) => {
            const key = `${roleName}/${relationshipId}`;
            rolesToReview.push({
              roleName: key,
              contact: c,
              index,
              responseUris: groupedResponses.responseUris.relationships[key],
              needReviewBeforeUpdate: contact.reviewBeforeUpdate?.[index],
            });
          });
        });
      },
    );

    const layoutItemIdsToGetContacts = new Set<string>();
    entriesOf(groupedResponses.layoutContacts).forEach(
      ([groupUri, inputContacts]) => {
        const group = groups?.find((g) => g.uri === groupUri);
        const layout = group?.values.layout;
        const field = group?.values.field;
        if (!layout || !field) return;

        const matchedLayoutItems = layoutItems?.filter(
          (item) => item.layoutDesign.id === layout.id && !item.isDeleted,
        );
        const layoutItemIndex =
          layout.parentId &&
          client?.role.name &&
          layout.parentId === client.role.name &&
          sentToIndividualClientIndex !== -1
            ? sentToIndividualClientIndex
            : 0;
        const layoutItemId = matchedLayoutItems?.[layoutItemIndex]?.itemId;
        if (layoutItemId) {
          layoutItemIdsToGetContacts.add(layoutItemId);
        }

        const roleName = `${layout.displayName || layout.name}/${field.displayName || field.name}`;
        inputContacts.forEach((contact, index) => {
          const indexedLayoutFieldName = buildIndexedLayoutFieldName({
            fieldName: field.name,
            originalFieldName: field.originalName || undefined,
            index,
          });

          rolesToReview.push({
            roleName,
            contact,
            index,
            responseUris:
              groupedResponses.responseUris.layoutContacts[groupUri],
            needReviewBeforeUpdate: contact.needReviewBeforeUpdate,
            indexedLayoutFieldName,
          });
        });
      },
    );
    setRoles(rolesToReview);
    setAutoResolvedKeys(new Set());

    if (layoutItemIdsToGetContacts.size && getLayoutContacts) {
      const loadedLayoutContacts = await Promise.all(
        Array.from(layoutItemIdsToGetContacts).map((itemId) =>
          getLayoutContacts(itemId),
        ),
      );
      setLayoutContacts(
        loadedLayoutContacts.reduce(
          (aggr, { layoutContacts: layoutContactsToAdd }) => {
            layoutContactsToAdd.forEach((contact) => {
              aggr[contact.key] = contact;
            });
            return aggr;
          },
          {} as Record<string, LayoutContact>,
        ),
      );
    }
    setIsLoading(false);
  }, [
    matter,
    sections,
    groups,
    fields,
    submission,
    layoutItems,
    formMatterTypeRepresentatives,
  ]);

  const foundRoles = useMemo(
    () =>
      roles.map((role) =>
        getRoleReviewInfo({
          role,
          roles,
          clientRoleName: client?.role.name,
          roleNameToDisplayNameMap,
          rolesAndRelationships,
          layoutContacts,
        }),
      ),
    [
      roles,
      client?.role.name,
      roleNameToDisplayNameMap,
      rolesAndRelationships,
      layoutContacts,
    ],
  );

  // Set reviewResult for roles that don't need review (skipped roles)
  useEffect(() => {
    const skippedRoleKeys = foundRoles
      .map((info, index) => {
        if (info !== null) return null;

        const role = roles[index];
        return getIndexedName(role.roleName, role.index);
      })
      .filter(isNotNullOrUndefined);

    if (skippedRoleKeys.length === 0) return;

    const missingKeys = skippedRoleKeys.filter((key) => !reviewResult[key]);
    if (missingKeys.length > 0) {
      setReviewResult((prev) => ({
        ...prev,
        ...Object.fromEntries(
          missingKeys.map((key) => [key, createNewContactPlaceholder]),
        ),
      }));
    }
  }, [foundRoles, roles, reviewResult]);

  const onSubmit = async () => {
    setIsSaving(true);

    // Use existing contact, update current contact or replace current contact
    const rolesOtherThanCreatingNewContact = entriesOf(
      groupBy(roles, (role) => role.roleName),
    )
      .map(([roleName, groupedRoles]) => {
        const existingItemIds = sortField(groupedRoles, {
          key: "index",
          dir: "asc",
        }).map((r) => reviewResult[getIndexedName(r.roleName, r.index)]);

        return {
          roleName,
          existingItemIds,
          responseUris: groupedRoles[0].responseUris,
        };
      })
      .filter(({ existingItemIds }) =>
        existingItemIds.some((id) => id && id !== createNewContactPlaceholder),
      );
    const responsesOtherThanCreatingNewContact =
      rolesOtherThanCreatingNewContact.flatMap(
        ({ responseUris, existingItemIds }) =>
          [...responseUris].map((uri) => ({ uri, existingItemIds })),
      );
    const allResponseUris = [
      ...new Set(roles.flatMap((r) => [...r.responseUris])),
    ];

    try {
      await Promise.all([
        ...responsesOtherThanCreatingNewContact.map(
          ({ uri, existingItemIds }) =>
            updateResponseExistingItemIds({
              variables: {
                uri,
                existingItemIds: JSON.stringify(existingItemIds),
              },
            }),
        ),
        ...allResponseUris.map((uri) =>
          updateResponseSyncStatus({
            variables: { uri, syncStatus: "reviewed" },
          }),
        ),
      ]);

      // Wait for the existing item ids to update
      const updatedSubmission = await backoff({
        run: () => querySubmission({ uri: submission.uri }),
        test: (found) => {
          if (!found?.responses.length) return false;

          const responsesMap = fromEntries(
            found.responses.map((r) => [r.uri, r]),
          );
          const isExistingItemIdsUpdated =
            rolesOtherThanCreatingNewContact.every(
              ({ responseUris, existingItemIds }) =>
                [...responseUris].some(
                  (uri) =>
                    responsesMap[uri]?.existingItemIds ===
                    JSON.stringify(existingItemIds),
                ),
            );
          const isSyncStatusUpdated = allResponseUris.every(
            (uri) => responsesMap[uri]?.syncStatus === "reviewed",
          );
          return isExistingItemIdsUpdated && isSyncStatusUpdated;
        },
        attempts: 4,
        base: 1000,
      })();

      if (updatedSubmission) {
        await sync({
          submission: mapToMatterSubmission(updatedSubmission),
          fields: fields || [],
          groups: groups || [],
          sections: sections || [],
          formMatterTypeRepresentatives,
        });
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      onFailure();
    }
  };

  const rolesRequireReview = foundRoles.filter((info) => info !== null);
  const filteredRolesRequireReview = rolesRequireReview.filter(
    (role) => !autoResolvedKeys.has(role.key),
  );
  const foundRolesRequireReview = filteredRolesRequireReview.length > 0;

  const isSubmitDisabled =
    loading ||
    isSaving ||
    rolesRequireReview.some((roleInfo) => !reviewResult[roleInfo.key]);

  const cancelButton = {
    text: "Cancel",
    variant: "secondarySubtle" as ButtonVariant,
    onClick: onClose,
  };

  const confirmButton = {
    text: foundRolesRequireReview ? "Confirm" : "Ok",
    disabled: isSubmitDisabled,
    onClick: onSubmit,
  };
  return (
    <Modal
      header={{
        icon: "solidTriangleExclamation",
        text: "Form Contact Review",
        iconBgColor: "bg-yellow-450",
      }}
      contentBgColor={foundRolesRequireReview ? "bg-neutral-50" : "bg-white"}
      size={foundRolesRequireReview ? "large" : "default"}
      subHeader={
        foundRolesRequireReview && (
          <div data-testid="contact-review-sub-header">
            This form contains{" "}
            <strong>
              {filteredRolesRequireReview.length}{" "}
              {pluralise(filteredRolesRequireReview, "contact")}
            </strong>{" "}
            that {pluralise(filteredRolesRequireReview, "has")} been changed or
            added and{" "}
            <strong>
              require{filteredRolesRequireReview.length === 1 ? "s" : ""} action
            </strong>{" "}
            before syncing to the matter
          </div>
        )
      }
      loading={loading || isSaving || isLoading}
      open={!loading}
      onClose={foundRolesRequireReview ? onClose : undefined}
      footer={{
        buttons: foundRolesRequireReview
          ? [cancelButton, confirmButton]
          : [confirmButton],
      }}
    >
      <div className="flex flex-col gap-8">
        {foundRolesRequireReview ? (
          (rolesRequireReview as RoleReviewInfo[]).map(
            ({
              key,
              displayRoleName,
              currentContactId,
              hasRoleContact,
              role,
            }) => (
              <ContactSelect
                key={key}
                roleName={displayRoleName}
                hasRoleContact={hasRoleContact}
                needReviewBeforeUpdate={role.needReviewBeforeUpdate}
                targetContact={role.contact}
                currentContactId={currentContactId}
                selected={reviewResult[key]}
                onSelect={(selected) =>
                  setReviewResult((result) => ({
                    ...result,
                    [key]: selected,
                  }))
                }
                onAutoResolve={() =>
                  setAutoResolvedKeys((prev) => new Set([...prev, key]))
                }
                getContact={getContact}
                openContact={openContact}
              />
            ),
          )
        ) : (
          <div>All duplicate contacts have been resolved.</div>
        )}
      </div>
    </Modal>
  );
};
